import dayjs from "dayjs";
import { type ISection } from "src/interfaces";
import { type IReportEntry } from "src/pages/Reports/Report/Report.types";
import {
  type IChart,
  type ILogbookCharts,
  type TSelectedCells,
} from "src/redux/slices/entriesGeneratorSlice";
import { getObjectKeys } from "src/utils";

import { isChartNumeric } from "../Charts/Chart.utils";
import {
  extractCellValue,
  findLeafColumn,
  findLeafColumnIndex,
  getCellAtLeafIndex,
  getEntryLocalDate,
  getNormalizedRows,
  getRowCols,
  getWorkingHeaders,
} from "./CreateChartButton.utils";

const DRAFT_STATUS = 0;
const UNVERIFIED_STATUSES = new Set([1, 2]); // Open, Re-Verify

export const isChartEntry = (
  entry: Pick<IReportEntry, "deleted" | "status">,
  withUnverified: boolean,
) =>
  Number(entry.deleted) !== 1 &&
  Number(entry.status) !== DRAFT_STATUS &&
  (withUnverified || !UNVERIFIED_STATUSES.has(Number(entry.status)));

export const hasChartSelection = (
  ...selections: Array<Record<string, Record<number, unknown[]>>>
) =>
  selections.some(selection =>
    Object.values(selection).some(log =>
      Object.values(log).some(picked => picked.length > 0),
    ),
  );

type TColumnData = Pick<
  IChart,
  | "name"
  | "sectionIndex"
  | "dataType"
  | "il"
  | "unit"
  | "vals"
  | "rawVals"
  | "rowRefs"
  | "dates"
>;

interface Params {
  entries: IReportEntry[];
  sections: Record<string, ISection[]>;
  selectedCols: Record<string, Record<number, string[]>>;
  selectedCells?: TSelectedCells;
  withUnverified: boolean;
}

export const buildChartData = ({
  entries,
  sections,
  selectedCols,
  selectedCells = {},
  withUnverified,
}: Params): ILogbookCharts[] => {
  const chartsDataObj: Record<string, Record<string, TColumnData>> = {};

  [
    ...new Set([
      ...getObjectKeys(selectedCols),
      ...getObjectKeys(selectedCells),
    ]),
  ].forEach((lbid: string) => {
    const logbookEntries = entries
      .filter(
        entry => entry.lbid === lbid && isChartEntry(entry, withUnverified),
      )
      .sort(
        (a, b) =>
          dayjs(a.datecreated).valueOf() - dayjs(b.datecreated).valueOf(),
      );

    [
      ...new Set(
        [
          ...getObjectKeys(selectedCols[lbid] ?? {}),
          ...getObjectKeys(selectedCells[lbid] ?? {}),
        ].map(Number),
      ),
    ].forEach(sectionIndex => {
      // A cell is a column read from one row only.
      const series: Array<{ name: string; col: string; rowIndex?: number }> = [
        ...(selectedCols[lbid]?.[sectionIndex] ?? []).map(col => ({
          name: col,
          col,
        })),
        ...(selectedCells[lbid]?.[sectionIndex] ?? []),
      ];
      if (!series.length) return;

      const sectionTemplate = sections[lbid]?.[sectionIndex];
      if (!sectionTemplate || sectionTemplate.subSections) return;
      const headers = getWorkingHeaders(sectionTemplate.headers);

      const colLeafIndices = series
        .map(({ name, col, rowIndex }) => ({
          colName: name,
          onlyRow: rowIndex,
          leafIndex: findLeafColumnIndex(headers, col),
          header: findLeafColumn(headers, col),
        }))
        .filter(({ leafIndex }) => leafIndex !== -1);
      if (!colLeafIndices.length) return;

      logbookEntries.forEach(entry => {
        const section = entry.lbentry.sections?.[sectionIndex];
        if (!section || section.subSections) return;
        const date = getEntryLocalDate(entry);

        // Normalize rows/cols once per entry instead of once per
        // selected column.
        getNormalizedRows(section.rows).forEach((row, rowIndex) => {
          const rowCols = getRowCols(row);

          colLeafIndices.forEach(({ colName, onlyRow, leafIndex, header }) => {
            const cell = getCellAtLeafIndex(rowCols, leafIndex);
            // Other rows stay as blanks, to keep the section index-aligned.
            const value =
              onlyRow === undefined || onlyRow === rowIndex
                ? extractCellValue(cell)
                : undefined;
            const key = `${sectionIndex}::${colName}`;

            if (!chartsDataObj[lbid]) chartsDataObj[lbid] = {};
            if (!chartsDataObj[lbid][key])
              chartsDataObj[lbid][key] = {
                name: colName,
                sectionIndex,
                dataType: header?.dataType,
                il: header?.il,
                unit: header?.unit,
                vals: [],
                rawVals: [],
                rowRefs: [],
                dates: [],
              };

            const column = chartsDataObj[lbid][key];
            // The header template does not always carry these; the cell does.
            column.il ??= cell?.il;
            column.unit ??= cell?.unit;

            column.rawVals.push(value);
            column.rowRefs.push({
              lbeid: entry.lbeid,
              rowIndex,
              date,
            });
            if (value === undefined) return;

            column.vals.push(value);
            column.dates.push(date);
          });
        });
      });
    });
  });

  return Object.entries(chartsDataObj)
    .map(([lbid, colsData]) => ({
      lbid,
      data: getObjectKeys(colsData)
        .filter(key => colsData[key].vals.length > 0)
        .map(key => ({
          ...colsData[key],
          id: crypto.randomUUID(),
          type: isChartNumeric(colsData[key])
            ? ("line" as const)
            : ("pie" as const),
          trendline: false,
        })),
    }))
    .filter(({ data }) => data.length > 0);
};
